import { useState, type FormEvent } from 'react';
import { useAppSelector } from '../../../Redux/hooks';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { ApiError } from '../../../Models/ApiModel';
import {
  PAYMENT_METHODS,
  type PaymentMethod,
  type ReceiptModel,
  type ReceiptPaymentModel
} from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './ReceiptEditor.css';

/** The payment-method labels, keyed the way the API spells them. */
const METHOD_KEYS: Record<PaymentMethod, TranslationKey> = {
  cash: 'methodCash',
  bank_transfer: 'methodBankTransfer',
  credit_card: 'methodCreditCard',
  bit: 'methodBit',
  paybox: 'methodPaybox',
  check: 'methodCheck',
  other: 'methodOther'
} as const;

interface ReceiptEditorProps {
  /** null writes a new receipt; a receipt edits or duplicates it. */
  receipt: ReceiptModel | null;
  /** A duplicate copies the fields and keeps none of the identity. */
  duplicate?: boolean;
  onClose: () => void;
  onSaved: (receipt: ReceiptModel) => void;
}

/**
 * Writing a receipt by hand, for the customer who is not on the roster: a
 * one-off workshop, a parent paying for a sibling, a lesson given as a favour
 * that still needs a document.
 *
 * A receipt written from lessons takes its amount from those lessons and this
 * form is not how it gets made. Here the amount is typed, which is why it is
 * required and why it is whole shekels like every other amount in the app.
 *
 * Only a draft can be edited. An issued receipt has a serial and frozen
 * snapshots behind it, and changing one after the fact is the thing the
 * numbering exists to prevent.
 *
 * There is no issue-date field, and that is deliberate. A document is dated
 * the day it is produced; the day the money arrived is a separate fact and
 * has its own field.
 */
export function ReceiptEditor({ receipt, duplicate = false, onClose, onSaved }: ReceiptEditorProps) {
  const { tr } = useTranslation();
  const students = useAppSelector(state => state.students.items);
  const editing = receipt !== null && !duplicate;

  const [student, setStudent] = useState(receipt?.student ?? '');
  const [customerName, setCustomerName] = useState(receipt?.customerName ?? '');
  const [customerAddress, setCustomerAddress] = useState(receipt?.customerAddress ?? '');
  const [customerTaxId, setCustomerTaxId] = useState(receipt?.customerTaxId ?? '');
  const [amount, setAmount] = useState(receipt?.amount ?? '');
  const [paymentDate, setPaymentDate] = useState(receipt?.paymentDate ?? '');
  const [description, setDescription] = useState(receipt?.description ?? '');
  const [notes, setNotes] = useState(receipt?.notes ?? '');
  const [payments, setPayments] = useState<ReceiptPaymentModel[]>(
    receipt?.payments && receipt.payments.length > 0
      ? receipt.payments.map(payment => ({ ...payment }))
      : [{ method: (receipt?.paymentMethod as PaymentMethod) ?? 'cash', amount: receipt?.amount ?? '' }]
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A single tender follows the total as it is typed, which is the common
  // case and saves the tutor entering the same number twice. Once the receipt
  // is split across two tenders the amounts are theirs to balance, and the
  // server refuses a set that does not add up.
  const single = payments.length === 1;
  const tendered = payments.reduce((total, payment) => total + (Number(payment.amount) || 0), 0);
  const balanced = single || tendered === (Number(amount) || 0);

  function setPayment(index: number, patch: Partial<ReceiptPaymentModel>) {
    setPayments(current => current.map((payment, at) => (at === index ? { ...payment, ...patch } : payment)));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const draft = {
      // The server accepts either, and uses the student's name as the customer
      // when only a student is given.
      ...(student ? { student } : {}),
      ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
      ...(customerAddress.trim() ? { customerAddress: customerAddress.trim() } : {}),
      ...(customerTaxId.trim() ? { customerTaxId: customerTaxId.trim() } : {}),
      amount,
      // No issue date: a document is dated the day it is produced, and that is
      // the server's clock rather than this form's.
      ...(paymentDate ? { paymentDate } : {}),
      payments: payments.map(payment => ({
        ...payment,
        amount: single ? amount : payment.amount
      })),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {})
    };
    try {
      const saved = editing
        ? await ReceiptService.updateDraft(receipt.id, draft)
        : await ReceiptService.createDraft(draft);
      onSaved(saved);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={editing ? tr('editReceipt') : duplicate ? tr('duplicateReceipt') : tr('newReceipt')}
      {...(duplicate && receipt?.receiptNumber ? { subtitle: receipt.receiptNumber } : {})}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="receipt-editor" className="primary-button" disabled={busy || !amount || !balanced || (!student && !customerName.trim())}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <form id="receipt-editor" className="receipt-editor" onSubmit={submit}>
        <label className="field">
          <span>{tr('studentName')}</span>
          <select className="text-input" value={student} onChange={e => setStudent(e.target.value)}>
            <option value="">{tr('oneOffCustomer')}</option>
            {students.map(candidate => (
              <option key={candidate.name} value={candidate.name}>{candidate.name}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{tr('customerName')}</span>
          <input
            className="text-input"
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            placeholder={student || undefined}
          />
        </label>
        <label className="field">
          <span>{tr('amount')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/\D/g, ''))}
            required
          />
        </label>
        <label className="field">
          <span>{tr('paymentDate')}</span>
          <input className="text-input" type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
        </label>
        <p className="field is-wide receipt-editor-hint">{tr('issueDateIsToday')}</p>
        <label className="field">
          <span>{tr('customerTaxId')}</span>
          <input className="text-input" value={customerTaxId} onChange={e => setCustomerTaxId(e.target.value)} />
        </label>
        <label className="field is-wide">
          <span>{tr('customerAddress')}</span>
          <input className="text-input" value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} />
        </label>

        {/* A receipt has to say how the money arrived, and for a cheque that
            is five separate details rather than a free-text note. */}
        <fieldset className="field is-wide receipt-editor-payments">
          <legend>{tr('paymentsTitle')}</legend>
          <p className="receipt-editor-hint">{tr('paymentsNote')}</p>
          {payments.map((payment, index) => (
            <div className="receipt-editor-payment" key={index}>
              <label className="field">
                <span>{tr('paymentMethod')}</span>
                <select
                  className="text-input"
                  value={payment.method}
                  onChange={e => setPayment(index, { method: e.target.value as PaymentMethod })}
                >
                  {PAYMENT_METHODS.map(method => (
                    <option key={method} value={method}>{tr(METHOD_KEYS[method])}</option>
                  ))}
                </select>
              </label>
              {!single && (
                <label className="field">
                  <span>{tr('paymentAmount')}</span>
                  <input
                    className="text-input"
                    inputMode="numeric"
                    value={payment.amount}
                    onChange={e => setPayment(index, { amount: e.target.value.replace(/\D/g, '') })}
                  />
                </label>
              )}
              {payment.method === 'check' && (
                <>
                  <label className="field">
                    <span>{tr('bankNumber')}</span>
                    <input className="text-input" inputMode="numeric" value={payment.bankNumber ?? ''}
                      onChange={e => setPayment(index, { bankNumber: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{tr('branchNumber')}</span>
                    <input className="text-input" inputMode="numeric" value={payment.branchNumber ?? ''}
                      onChange={e => setPayment(index, { branchNumber: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{tr('accountNumber')}</span>
                    <input className="text-input" inputMode="numeric" value={payment.accountNumber ?? ''}
                      onChange={e => setPayment(index, { accountNumber: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{tr('chequeNumber')}</span>
                    <input className="text-input" inputMode="numeric" value={payment.chequeNumber ?? ''}
                      onChange={e => setPayment(index, { chequeNumber: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{tr('chequeDueDateField')}</span>
                    <input className="text-input" type="date" value={payment.dueDate ?? ''}
                      onChange={e => setPayment(index, { dueDate: e.target.value })} />
                  </label>
                </>
              )}
              {payment.method === 'credit_card' && (
                <>
                  <label className="field">
                    <span>{tr('cardBrand')}</span>
                    <input className="text-input" value={payment.cardBrand ?? ''}
                      onChange={e => setPayment(index, { cardBrand: e.target.value })} />
                  </label>
                  <label className="field">
                    <span>{tr('cardLast4')}</span>
                    <input className="text-input" inputMode="numeric" maxLength={4} value={payment.cardLast4 ?? ''}
                      onChange={e => setPayment(index, { cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
                  </label>
                </>
              )}
              {!['check', 'credit_card'].includes(payment.method) && (
                <label className="field">
                  <span>{tr('paymentReference')}</span>
                  <input className="text-input" value={payment.reference ?? ''}
                    onChange={e => setPayment(index, { reference: e.target.value })} />
                </label>
              )}
              {payments.length > 1 && (
                <button
                  type="button"
                  className="ghost-button receipt-editor-remove"
                  onClick={() => setPayments(current => current.filter((_, at) => at !== index))}
                >
                  {tr('removePayment')}
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="ghost-button"
            onClick={() => setPayments(current => [
              // The first split keeps what was already entered on the single
              // line, so adding a second tender never silently blanks the first.
              ...current.map((payment, at) => (at === 0 && current.length === 1
                ? { ...payment, amount: payment.amount || amount }
                : payment)),
              { method: 'cash' as PaymentMethod, amount: '' }
            ])}
          >
            {tr('addPayment')}
          </button>
          {!balanced && <p className="receipt-editor-error">{tr('paymentsBalanceHint')}</p>}
        </fieldset>
        <label className="field is-wide">
          <span>{tr('receiptDescription')}</span>
          <input className="text-input" value={description} onChange={e => setDescription(e.target.value)} />
        </label>
        <label className="field is-wide">
          <span>{tr('notes')}</span>
          <textarea className="text-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
        </label>
        {error && <p className="receipt-editor-error" role="alert">{tr(ERROR_KEYS[error] ?? 'errorGeneric')}</p>}
      </form>
    </Modal>
  );
}

/** Specific codes get specific copy; anything else falls back to the generic. */
const ERROR_KEYS: Record<string, TranslationKey> = {
  INVALID_CUSTOMER_TAX_ID: 'errorInvalidCustomerTaxId',
  PAYMENTS_DO_NOT_BALANCE: 'errorPaymentsDoNotBalance',
  PAYMENT_DETAILS_REQUIRED: 'errorPaymentDetailsRequired',
  INVALID_PAYMENT_AMOUNT: 'errorPaymentsDoNotBalance'
};
